import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

/**
 * "Come back here after signing in": login and register pages carry the page
 * the visitor wanted as ?redirect=/path (set by route guards, the server's
 * login redirect and the website's buttons). Only same-site paths are used.
 */
export const REDIRECT_PARAM = 'redirect';

export function safeRedirectPath(value: string | null | undefined): string | null {
  if (!value || value.length > 500) return null;
  // A path on this site: "/x", never "//host", "/\\host" or "scheme:".
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return null;
  }
  // Never bounce back to the auth pages themselves.
  if (/^\/(login|register|forgot-password|password\/reset)(\/|\?|$)/.test(value)) {
    return null;
  }
  return value;
}

/** The ?redirect target of the current page, if it is a safe path. */
export function currentRedirectParam(): string | null {
  if (typeof window === 'undefined') return null;
  return safeRedirectPath(new URLSearchParams(window.location.search).get(REDIRECT_PARAM));
}

/** The current page as a router path (without the site's base folder). */
export function currentAppPath(): string {
  if (typeof window === 'undefined') return '/';
  let base = '';
  try {
    base = new URL(getBootstrapData().settings.base_url).pathname.replace(/\/$/, '');
  } catch {
    // keep base empty
  }
  let path = window.location.pathname;
  if (base && (path === base || path.startsWith(base + '/'))) {
    path = path.slice(base.length) || '/';
  }
  return path + window.location.search;
}

/** "/login" (or "/register") that returns to the given path afterwards. */
export function authPathWithRedirect(authPath: '/login' | '/register', target: string | null): string {
  const safe = safeRedirectPath(target);
  return safe ? `${authPath}?${REDIRECT_PARAM}=${encodeURIComponent(safe)}` : authPath;
}

/** Keeps the current ?redirect when moving between login and register. */
export function withCurrentRedirect(authPath: '/login' | '/register'): string {
  return authPathWithRedirect(authPath, currentRedirectParam());
}
